import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Stack,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { PRICING } from "../config/pricing";

function GradientDialogTitle({ title }) {
  return (
    <Box sx={{ pr: 5 }}>
      <Typography
        component="h2"
        sx={{
          fontSize: { xs: "1.35rem", sm: "1.7rem" },
          lineHeight: 1.15,
          fontWeight: 900,
          background: "linear-gradient(135deg, #7d9c14 0%, #5d770a 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function ModalShell({
  open,
  onClose,
  title,
  children,
  canClose = true,
  onBlockedClose,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDialogClose = (event, reason) => {
    if (!canClose && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      onBlockedClose?.(reason);
      return;
    }
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={!canClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pb: 1.2,
        }}
      >
        <GradientDialogTitle title={title} />

        <IconButton
          onClick={() => {
            if (!canClose) return onBlockedClose?.("xClick");
            onClose?.();
          }}
          aria-label="Popup schließen"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "action.hover",
            "&:hover": { bgcolor: "action.selected" },
          }}
        >
          <Typography sx={{ fontSize: 22, lineHeight: 1, color: "text.secondary" }}>
            ×
          </Typography>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 4, sm: 5, md: 5 } }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default function PurchaseSuccessDialog({
  open,
  onClose,
  token,
  basePath = "/mediakit/generator/",
}) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [needsCopyHint, setNeedsCopyHint] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [copyToast, setCopyToast] = useState(false); // ✅ 2s Feedback im Button

  const accessUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const origin = window.location.origin;
    const url = new URL(basePath, origin);
    if (token) url.searchParams.set("token", token);
    return url.toString();
  }, [token, basePath]);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    setNeedsCopyHint(false);
    setCopyError("");
    setCopyToast(false);
  }, [open]);

  // ✅ META PIXEL (optional)
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return;

    const sp = new URLSearchParams(window.location.search);
    const stripeSessionId = sp.get("session_id");
    const eventId = stripeSessionId || token || `purchase_${Date.now()}`;

    const storageKey = `fb_purchase_fired_${eventId}`;
    try {
      if (sessionStorage.getItem(storageKey) === "1") return;
      sessionStorage.setItem(storageKey, "1");
    } catch {}

    window.fbq(
      "track",
      "Purchase",
      { value: Number(PRICING.price), currency: PRICING.currency || "EUR" },
      { eventID: eventId }
    );
  }, [open, token]);

  const copyFallback = (text) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const copyToClipboard = async () => {
    if (!accessUrl) return false;

    setCopyError("");
    setNeedsCopyHint(false);

    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(accessUrl);
        ok = true;
      }
    } catch {
      ok = false;
    }

    if (!ok) ok = copyFallback(accessUrl);

    if (ok) {
      setCopied(true);

      // ✅ Button-Feedback 2 Sekunden
      setCopyToast(true);
      window.setTimeout(() => setCopyToast(false), 2000);

      return true;
    }

    setCopyError(
      "Kopieren hat nicht geklappt. Bitte markiere den Link im Feld und kopiere ihn manuell (Strg/Cmd + C)."
    );
    return false;
  };

  const handlePrimary = async () => {
    if (!copied) {
      await copyToClipboard();
      return;
    }
    if (accessUrl) window.location.href = accessUrl;
    else onClose?.();
  };

  const canClose = copied;

  const buttonLabel = copied
    ? copyToast
      ? "Link kopiert ✅"
      : "Jetzt loslegen"
    : copyToast
    ? "Link kopiert ✅"
    : "Zugangslink kopieren";

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      canClose={canClose}
      onBlockedClose={() => setNeedsCopyHint(true)}
      title="Zugang freigeschaltet 🎉"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Herzlichen Dank für deinen Kauf. Das ist dein persönlicher Zugangscode. Du bekommst in
          den nächsten Stunden die Rechnung per Mail gesendet, auf der dein Zugangscode nochmals
          ausgewiesen ist.
        </Typography>

        <Alert severity="warning" variant="outlined">
          <Typography variant="body2">
            <b>Wichtig:</b> Der Zugangslink ist personalisiert und nur für deine eigene Nutzung
            gedacht. Bitte nicht weitergeben, teilen oder öffentlich posten. Speichere ihn gut ab,
            bevor du loslegst.
          </Typography>
        </Alert>

        <TextField
          label="Dein Zugangslink"
          value={accessUrl}
          fullWidth
          size="small"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Tooltip title="Link kopieren">
                <IconButton onClick={copyToClipboard} edge="end">
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          }}
        />

        {copyError ? (
          <Alert severity="error" variant="outlined">
            {copyError}
          </Alert>
        ) : null}

        <Button
          onClick={handlePrimary}
          variant="contained"
          fullWidth
          color="primary"
          sx={{
            py: 1.2,
            ...(needsCopyHint && !copied
              ? { boxShadow: `0 0 0 3px ${theme.palette.error.main}` }
              : {}),
          }}
        >
          {buttonLabel}
        </Button>

        {needsCopyHint && !copied ? (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            Bitte zuerst den Zugangslink kopieren und speichern.
          </Typography>
        ) : null}
      </Stack>
    </ModalShell>
  );
}
